import produce from 'immer'
import { useEffect, useMemo, useState } from 'react'
import { QueryStatus } from 'react-query'
import { UseInfiniteQueryResult } from 'react-query/types/react/types'
import create from 'zustand'
import shallow from 'zustand/shallow'

import { useGremlinPagination } from 'hooks/use-gremlin-query'
import { useHasFlagChangedFrom } from 'hooks/use-has-flag-changed'
import { usePagedQueryScaledCaching } from 'hooks/use-paged-query-scaled-caching'
import { PagedTeamsResponse, Team } from 'types/api/teams'

export interface TeamsState {
  status: QueryStatus
  teams: {
    [id: string]: Team
  }
  hasNextPage: boolean
  set: (fn: (state: TeamsState) => void) => void
  clearAll: () => void
}

// team data is kept in a zustand store using immer to mutate the object only when there are data changes
// this prevents unnecessary re-renders in components using the team data
export const useTeamsStore = create<TeamsState>((set) => ({
  status: 'loading',
  teams: {},
  hasNextPage: true,
  set: (fn) => set(produce(fn)),
  clearAll: () =>
    set(
      produce((state) => {
        state.status = 'loading'
        state.teams = {}
      })
    )
}))

const QUERY_KEY_PAGED_TEAMS = 'paged-teams'

/**
 * useGetTeamsPaged is intended to handle retrieving the entire team list from the paged /v1/teams/paged endpoint
 * while also managing performance issues related to this list potentially being very large
 * It will somewhat intelligently alter the cache time and how often it refreshes the data based on its existing size
 * This will hopefully improve perceived performance in the webapp for customers with large numbers of teams
 * and stop the app from locking up on long api calls
 *
 * This hook will be called by useLongRunningApiData to kick off the retrieval process when the webapp loads
 * It is not intended to be called in other places
 */
export function useGetTeamsPaged(
  additionalOptions = {}
): UseInfiniteQueryResult<PagedTeamsResponse> {
  const set = useTeamsStore((state) => state.set)
  const { cacheTTL, setNumPages } = usePagedQueryScaledCaching()
  const [pageSize, setPageSize] = useState(500)

  const pagedAllFetchState = useGremlinPagination<PagedTeamsResponse>(
    `/teams/paged?pageSize=${pageSize}`, // it is important to use the named query string because this query url may change
    {},
    {
      ...additionalOptions,
      // do not spread additionalOptions after these as we do not want them overwritten
      onSuccess: (data) => {
        // update how many pages of data we've gotten so we dynamically update how long we cache it
        setNumPages(data?.pages?.length || 0)

        // update the team list in the store if necessary
        set((state) => {
          const all = data?.pages?.flatMap((page) => page.items)
          state.teams = {
            ...Object.fromEntries(all.map((s) => [s.identifier, s]))
          }
        })
      },
      onSettled: (data, error) => {
        set((state) => {
          if (error && (!data || typeof data === 'string')) {
            state.status = 'error'
            return
          }

          // state.hasNextPage is not updated until after the fact so it's not a good indicator here
          // instead we use the data directly from the response, and if it contains another page
          // then we know we're still loading
          const doesDataHaveAnotherPageToken = Boolean(
            // get last page of data's pageToken
            data.pages.at(-1).pageToken
          )
          if (doesDataHaveAnotherPageToken) {
            state.status = 'loading'
            return
          }

          state.status = 'success'
        })
      },
      // wait this long before refetching
      staleTime: cacheTTL,
      // refetch at least every 15 minutes
      refetchInterval: 1_000 * 60 * 15
    },
    // use a named queryKey instead of the endpoint so we can use mutations in the future
    QUERY_KEY_PAGED_TEAMS
  )

  // update paging state
  useEffect(() => {
    let timeout: NodeJS.Timeout
    if (
      pagedAllFetchState.hasNextPage &&
      !pagedAllFetchState.isFetchingNextPage
    ) {
      set((state) => {
        state.hasNextPage = true
      })
      timeout = setTimeout(() => {
        pagedAllFetchState.fetchNextPage()
      }, 50) // starting with this - it may need to be increased if we encounter problems
    } else if (!pagedAllFetchState.hasNextPage) {
      set((state) => {
        state.hasNextPage = false
      })

      if (!pagedAllFetchState.isFetching) {
        // dynamically update pageSize based on how many teams there are
        // so when we refresh the list we do it more optimally
        // TODO any need to store this in local storage?
        const numTeams = pagedAllFetchState.data?.pages?.flatMap(
          (p) => p.items
        ).length

        if (numTeams < 1000) {
          if (pageSize !== 100) {
            setPageSize(100)
          }
        } else {
          if (pageSize !== 500) {
            setPageSize(500)
          }
        }
      }
    }
    return () => clearTimeout(timeout)
  }, [pagedAllFetchState, set, pageSize, setPageSize])

  return pagedAllFetchState
}

/**
 * useGetTeams is the intended hook to be called by pages in the webapp that need team data
 */
export function useGetTeams() {
  const { refetch } = useGetTeamsPaged()

  const teamState = useTeamsStore(
    (state) => ({ teams: state.teams, status: state.status }),
    shallow
  )

  const teams = useMemo(
    () => Object.entries(teamState.teams).map(([, value]) => value),
    [teamState.teams]
  )

  // track whether we've loaded once so we don't re-display loading state when refetching data
  const hasLoadedOnce = useHasFlagChangedFrom({
    flag: teamState.status,
    expectedStartValue: 'loading'
  })

  // this is intended to reflect an adorned subset the react-query api
  return {
    data: teams,
    status: teamState.status,
    isFetching: teamState.status === 'loading',
    isError: teamState.status === 'error',
    refetch,
    hasLoadedOnce
  }
}
